// Manual "Refresh IntelliSense" — the one entry point behind Ctrl+Shift+R, the
// Command Palette entry, the Object Explorer's Refresh action, the
// status-bar pill's "Reload schema" button and the Visual Query Editor
// banner's "Reload schema" button.
//
// It re-reads the schema behind autocomplete for ONE `(connectionId,
// database)`: the derived per-connection caches that the post-DDL path
// (`ddlInvalidator.ts`) drops selectively are dropped unconditionally here —
// a user asking for a refresh has no SQL for us to classify, they simply
// know the schema changed outside the app — and then the engine's own schema
// cache is force-refreshed through `reloadSchemaSnapshot`, which republishes
// to the schema bus. The in-flight window is recorded in
// `schemaRefreshStore` so every surface showing that database can render its
// spinner. Target resolution (active tab / explorer node / whole connection)
// lives in `refreshTargets.ts`.

import type { DatabaseEngineId } from '@/core/engines';

import { reloadSchemaSnapshot } from '../../connections/services/language-services/reloadSchemaSnapshot';
import { invalidateColumns } from '../../workspace/common/utils/insertHintColumnResolver';
import { invalidateRoutines } from '../../workspace/common/utils/routineParameterResolver';
import { dropMssqlColumnCache } from '../resolvers/mssqlColumnCache';

import { isSchemaRefreshing, schemaRefreshKey, useSchemaRefreshStore } from './schemaRefreshStore';

/** Where the refresh was asked for — the analytics `source` property. */
export type IntelliSenseRefreshSource = 'shortcut' | 'palette' | 'explorer' | 'pill' | 'vqe';

export type IntelliSenseRefreshOutcome =
  /** The engine's schema was re-read and republished. */
  | 'refreshed'
  /** Nothing to reload: the mock engine, or Kusto with no document open on the pair. */
  | 'unsupported'
  /** A refresh for this `(connectionId, database)` was already running. */
  | 'already-running';

export type RefreshIntelliSenseParams = {
  connectionId: string;
  database: string;
  engine: DatabaseEngineId;
  source: IntelliSenseRefreshSource;
};

/**
 * The promise of every refresh still in flight, so a caller that is told
 * `'already-running'` can wait for that run instead of starting work that
 * would overlap it (`whenSchemaRefreshSettled`).
 */
const inFlightRuns = new Map<string, Promise<IntelliSenseRefreshOutcome>>();

function trackRefresh(engine: DatabaseEngineId, source: IntelliSenseRefreshSource): void {
  if (typeof window === 'undefined' || !window.electronAPI?.analytics?.track) return;
  window.electronAPI.analytics
    .track('feature:intellisense_schema_reload', { engine, source })
    .catch(() => {
      // Analytics must never affect UX.
    });
}

/**
 * Drops the per-connection caches derived FROM the schema snapshot — the
 * same drops `invalidateAfterDdl` performs, minus its SQL-shape gating. The
 * native engine caches are not dropped here: `reloadSchemaSnapshot`
 * refetches them with `forceRefresh: true`. The resolver's `notifyDdl` is
 * deliberately NOT sent either: it clears the SchemaSnapshot of every VQE
 * context on the connection, and only contexts on the refreshed database
 * re-pull (they subscribe to the bus `updated` event for their own database,
 * which the refetch emits) — a refresh of database A must not blank an open
 * Visual Query Editor on database B.
 */
function dropDerivedSchemaCaches(connectionId: string): void {
  invalidateColumns(connectionId);
  invalidateRoutines(connectionId);
  dropMssqlColumnCache(connectionId);
}

/** Resolves when no refresh for the pair is in flight (never rejects). */
export function whenSchemaRefreshSettled(connectionId: string, database: string): Promise<void> {
  const run = inFlightRuns.get(schemaRefreshKey(connectionId, database));
  return run ? run.then(() => undefined, () => undefined) : Promise.resolve();
}

/**
 * Force-refresh the IntelliSense schema for `(connectionId, database)`.
 * Rejects with whatever the engine cache rejects with (after closing the
 * in-flight window and remembering the message) so the caller can surface
 * the failure.
 */
export function refreshIntelliSense(params: RefreshIntelliSenseParams): Promise<IntelliSenseRefreshOutcome> {
  const { connectionId, database, engine, source } = params;
  if (isSchemaRefreshing(connectionId, database)) return Promise.resolve('already-running');

  const key = schemaRefreshKey(connectionId, database);
  const store = useSchemaRefreshStore.getState();
  const token = store.begin(connectionId, database);
  trackRefresh(engine, source);

  const run = (async (): Promise<IntelliSenseRefreshOutcome> => {
    try {
      dropDerivedSchemaCaches(connectionId);
      const reloaded = await reloadSchemaSnapshot({ connectionId, database, engine });
      store.end(connectionId, database, token);
      return reloaded ? 'refreshed' : 'unsupported';
    } catch (error: unknown) {
      store.end(
        connectionId,
        database,
        token,
        error instanceof Error ? error.message : 'Failed to reload database schema',
      );
      throw error;
    }
  })();

  inFlightRuns.set(key, run);
  run.then(
    () => { if (inFlightRuns.get(key) === run) inFlightRuns.delete(key); },
    () => { if (inFlightRuns.get(key) === run) inFlightRuns.delete(key); },
  );
  return run;
}
